import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import { ArrowRight, BookOpen, Rocket, TrendingUp, Shield, Sparkles } from "lucide-react";

import PortraitWithBlob from "@/components/common/PortraitWithBlob";
import SectionHeading from "@/components/common/SectionHeading";
import NewsletterForm from "@/components/common/NewsletterForm";
import Pill from "@/components/common/Pill";
import PillarCard from "@/components/cards/PillarCard";
import ProcessStep from "@/components/cards/ProcessStep";
import ContentCard from "@/components/cards/ContentCard";
import Reveal from "@/components/common/Reveal";
import WordsReveal from "@/components/motion/WordsReveal";
import MagneticButton from "@/components/motion/MagneticButton";
import ParallaxPortrait from "@/components/motion/ParallaxPortrait";
import PathLine from "@/components/motion/PathLine";
import SceneBackdrop from "@/components/motion/SceneBackdrop";
import SparkleEyebrow from "@/components/motion/SparkleEyebrow";
import AnimatedMesh from "@/components/motion/AnimatedMesh";
import HoverGlow from "@/components/motion/HoverGlow";
import GradientText from "@/components/motion/GradientText";
import ASSETS from "@/lib/assets";

const featured = (t) => [
  { typeKey: "featured1Type", titleKey: "featured1Title", descKey: "featured1Desc", platformKey: "featured1Platform", image: ASSETS.editorial[0] },
  { typeKey: "featured2Type", titleKey: "featured2Title", descKey: "featured2Desc", platformKey: "featured2Platform", image: ASSETS.editorial[3] },
  { typeKey: "featured3Type", titleKey: "featured3Title", descKey: "featured3Desc", platformKey: "featured3Platform", image: ASSETS.editorial[6] },
  { typeKey: "featured4Type", titleKey: "featured4Title", descKey: "featured4Desc", platformKey: "featured4Platform", image: ASSETS.editorial[1] },
];

const TrustCard = ({ icon: Icon, label, accent, delay, to }) => {
  const reduced = usePreferStaticUI();
  const inner = (
    <>
      <div className={`w-9 h-9 rounded-lg ${accent} flex items-center justify-center text-plum shrink-0`}>
        <Icon size={16} strokeWidth={1.6} />
      </div>
      <p className="text-sm font-medium text-ink/85">{label}</p>
    </>
  );

  const className = `wit-glass flex items-center gap-3 px-5 py-4 ${to ? "cursor-pointer hover:border-raspberry/20" : "cursor-default"}`;

  if (reduced) {
    return (
      <div className={className}>
        {to ? (
          <Link to={to} className="flex items-center gap-3 w-full">
            {inner}
          </Link>
        ) : (
          inner
        )}
      </div>
    );
  }

  const animatedInner = (
    <>
      <motion.div
        className={`w-9 h-9 rounded-lg ${accent} flex items-center justify-center text-plum shrink-0`}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: delay * 2 }}
      >
        <Icon size={16} strokeWidth={1.6} />
      </motion.div>
      <p className="text-sm font-medium text-ink/85">{label}</p>
    </>
  );

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 0.61, 0.36, 1] }}
      whileHover={{ y: -3, transition: { duration: 0.25 } }}
    >
      {to ? (
        <Link to={to} className="flex items-center gap-3 w-full">
          {animatedInner}
        </Link>
      ) : (
        animatedInner
      )}
    </motion.div>
  );
};

export default function Home() {
  const { t } = useTranslation();
  const reduced = usePreferStaticUI();
  const headline = t("home.heroHeadline");
  const headlineParts = headline.split(/(?<=[.!?])\s+/);
  const leadLine = headlineParts[0] || headline;
  const accentLine = headlineParts.slice(1).join(" ");

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <SceneBackdrop variant="hero" sparkleCount={8} mesh meshPalette="rose" />
        <div className="wit-section-wide relative pt-6 lg:pt-10 pb-12 lg:pb-14">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal y={8} amount={0.05}>
                <SparkleEyebrow>{t("meta.tagline")}</SparkleEyebrow>
              </Reveal>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.04] text-ink" data-testid="home-hero-headline">
                <WordsReveal
                  as="span"
                  text={leadLine}
                  className="block"
                  stagger={0.07}
                  duration={0.7}
                  delay={0.05}
                />
                {accentLine && (
                  <span className="block mt-2">
                    <GradientText className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.04]">
                      {accentLine}
                    </GradientText>
                  </span>
                )}
              </h1>

              {!reduced && (
                <motion.div
                  className="mt-8 h-px max-w-lg bg-gradient-to-r from-transparent via-raspberry/35 to-transparent"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.4, delay: 0.6, ease: "easeOut" }}
                  style={{ originX: 0 }}
                />
              )}

              <Reveal delay={0.45} amount={0.05}>
                <p className="mt-6 text-base sm:text-lg text-warmGrey leading-relaxed max-w-xl">
                  {t("home.heroSubline")}
                </p>
              </Reveal>
              <Reveal delay={0.58} amount={0.05}>
                <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
                  <MagneticButton as={Link} to="/submit-startup" className="wit-btn-primary" data-testid="hero-cta-submit" strength={0.32}>
                    {t("home.ctaSubmit")} <ArrowRight size={16} />
                  </MagneticButton>
                  <Link to="/investors" className="wit-btn-secondary" data-testid="hero-cta-investor">
                    {t("home.ctaInvestor")}
                  </Link>
                  <Link to="/content" className="wit-btn-secondary" data-testid="hero-cta-stories">
                    {t("home.ctaStories")} <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.7} amount={0.05}>
                {reduced ? (
                  <div className="mt-8 flex flex-wrap gap-2" data-testid="hero-badges">
                    {[
                      ["badge-founder", "bg-roseMilk", "border-roseMilk", "badges.founder"],
                      ["badge-investor", "bg-lavenderFog", "border-lavenderFog", "badges.investor"],
                      ["badge-creator", "bg-butterCream", "border-butterCream", "badges.creator"],
                      ["badge-journalist", "bg-sageMint", "border-sageMint", "badges.journalist"],
                      ["badge-matchmaker", "bg-skyMist/60", "border-skyMist", "badges.matchmaker"],
                      ["badge-voice", "bg-roseMilk/50", "border-roseMilk", "badges.voice"],
                    ].map(([id, accent, border, key]) => (
                      <Pill key={id} accent={accent} border={border} testId={id}>{t(key)}</Pill>
                    ))}
                  </div>
                ) : (
                <motion.div
                  className="mt-8 flex flex-wrap gap-2"
                  data-testid="hero-badges"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={{
                    hidden: {},
                    show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
                  }}
                >
                  {[
                    ["badge-founder", "bg-roseMilk", "border-roseMilk", "badges.founder"],
                    ["badge-investor", "bg-lavenderFog", "border-lavenderFog", "badges.investor"],
                    ["badge-creator", "bg-butterCream", "border-butterCream", "badges.creator"],
                    ["badge-journalist", "bg-sageMint", "border-sageMint", "badges.journalist"],
                    ["badge-matchmaker", "bg-skyMist/60", "border-skyMist", "badges.matchmaker"],
                    ["badge-voice", "bg-roseMilk/50", "border-roseMilk", "badges.voice"],
                  ].map(([id, accent, border, key]) => (
                    <motion.div
                      key={id}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                      }}
                    >
                      <Pill accent={accent} border={border} testId={id}>{t(key)}</Pill>
                    </motion.div>
                  ))}
                </motion.div>
                )}
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <ParallaxPortrait strength={reduced ? 0 : -30} className="relative">
                <PortraitWithBlob size="max-w-sm lg:max-w-md mx-auto" testId="hero-portrait" />
              </ParallaxPortrait>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <TrustCard icon={Rocket} label={t("home.trustFounder")} accent="bg-roseMilk/70" delay={0} to="/startups" />
            <TrustCard icon={TrendingUp} label={t("home.trustInvestor")} accent="bg-lavenderFog/70" delay={0.08} to="/investors" />
            <TrustCard icon={Shield} label={t("home.trustEyebrow")} accent="bg-sageMint/70" delay={0.16} />
            <TrustCard icon={Sparkles} label={t("badges.voice")} accent="bg-butterCream/80" delay={0.24} />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="wit-section-block relative">
        <Reveal>
          <div className="relative wit-glass-strong p-8 sm:p-12 text-center overflow-hidden" data-testid="mission-block">
            <SparkleEyebrow className="wit-eyebrow text-raspberry justify-center mb-4">{t("home.missionEyebrow")}</SparkleEyebrow>
            <h2 className="wit-heading-display max-w-3xl mx-auto">{t("home.missionTitle")}</h2>
            <p className="mt-5 wit-body max-w-2xl mx-auto sm:text-base">
              {t("home.missionBody")}
            </p>
          </div>
        </Reveal>
      </section>

      {/* PILLARS */}
      <section className="wit-section-block relative">
        <Reveal>
          <SectionHeading
            eyebrow={t("home.pillarsEyebrow")}
            title={t("home.pillarsTitle")}
            align="center"
            className="mb-8"
          />
        </Reveal>
        <div className="grid md:grid-cols-6 gap-3 auto-rows-fr">
          <Reveal delay={0.05} className="md:col-span-3 md:row-span-2">
            <HoverGlow className="h-full">
              <PillarCard icon={BookOpen} title={t("home.pillar1Title")} body={t("home.pillar1Body")} accent="bg-lavenderFog" testId="pillar-stories" />
            </HoverGlow>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-3">
            <HoverGlow className="h-full">
              <PillarCard icon={Rocket} title={t("home.pillar2Title")} body={t("home.pillar2Body")} accent="bg-sageMint" testId="pillar-startups" />
            </HoverGlow>
          </Reveal>
          <Reveal delay={0.19} className="md:col-span-3">
            <HoverGlow className="h-full">
              <PillarCard icon={TrendingUp} title={t("home.pillar3Title")} body={t("home.pillar3Body")} accent="bg-skyMist" testId="pillar-capital" />
            </HoverGlow>
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      <section className="wit-section-block">
        <Reveal>
          <SectionHeading
            eyebrow={t("home.featuredEyebrow")}
            title={t("home.featuredTitle")}
            subtitle={t("home.featuredSubtitle")}
            className="mb-8"
          />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="featured-grid">
          {featured(t).map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <HoverGlow>
                <ContentCard
                  image={f.image}
                  type={t(`home.${f.typeKey}`)}
                  title={t(`home.${f.titleKey}`)}
                  description={t(`home.${f.descKey}`)}
                  platform={t(`home.${f.platformKey}`)}
                  comingSoon={t("home.featuredBadge")}
                  testId={`featured-card-${i}`}
                />
              </HoverGlow>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PATHS */}
      <section className="wit-section-block">
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="relative wit-glass-strong p-6 sm:p-10 overflow-hidden">
            <AnimatedMesh palette="sage" opacity={0.3} className="absolute inset-0 -z-10" />
            <Reveal>
              <SectionHeading eyebrow={t("home.startupsEyebrow")} title={t("home.startupsTitle")} className="mb-8" />
            </Reveal>
            <div className="relative grid sm:grid-cols-2 gap-3">
              <PathLine className="hidden sm:block absolute top-1/2 left-[20%] right-[20%] h-px -translate-y-1/2 pointer-events-none" duration={1.6} />
              {[1, 2, 3, 4].map((idx) => (
                <Reveal key={idx} delay={(idx - 1) * 0.08}>
                  <ProcessStep
                    index={`${idx}`}
                    title={t(`home.startupsStep${idx}`)}
                    body={t(`home.startupsStep${idx}Body`)}
                    accent="bg-sageMint"
                    testId={`home-startups-${idx}`}
                  />
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.45}>
              <div className="mt-8">
                <MagneticButton as={Link} to="/startups" className="wit-btn-primary" data-testid="home-startups-cta">
                  {t("home.startupsCta")} <ArrowRight size={16} />
                </MagneticButton>
              </div>
            </Reveal>
          </div>

          <div className="relative wit-glass-strong p-6 sm:p-10 overflow-hidden">
            <AnimatedMesh palette="sky" opacity={0.3} className="absolute inset-0 -z-10" duration={30} />
            <Reveal>
              <SectionHeading eyebrow={t("home.investorsEyebrow")} title={t("home.investorsTitle")} className="mb-8" />
            </Reveal>
            <div className="relative grid sm:grid-cols-2 gap-3">
              <PathLine className="hidden sm:block absolute top-1/2 left-[20%] right-[20%] h-px -translate-y-1/2 pointer-events-none" duration={1.6} delay={0.2} />
              {[1, 2, 3, 4].map((idx) => (
                <Reveal key={idx} delay={(idx - 1) * 0.08}>
                  <ProcessStep
                    index={`${idx}`}
                    title={t(`home.investorsStep${idx}`)}
                    body={t(`home.investorsStep${idx}Body`)}
                    accent="bg-skyMist"
                    testId={`home-investors-${idx}`}
                  />
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.45}>
              <div className="mt-8">
                <MagneticButton as={Link} to="/investors" className="wit-btn-primary" data-testid="home-investors-cta">
                  {t("home.investorsCta")} <ArrowRight size={16} />
                </MagneticButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="wit-section-block relative">
        <Reveal>
          <div className="relative wit-glass-strong p-8 sm:p-12 lg:p-14 text-center overflow-hidden" data-testid="trust-block">
            <SparkleEyebrow className="wit-eyebrow text-raspberry justify-center mb-4">{t("home.trustEyebrow")}</SparkleEyebrow>
            <blockquote className="wit-heading-display max-w-3xl mx-auto font-serif" data-testid="trust-quote">
              &ldquo;{t("home.trustQuote")}&rdquo;
            </blockquote>
            <p className="mt-5 wit-body max-w-2xl mx-auto sm:text-base">
              {t("home.trustBody")}
            </p>
          </div>
        </Reveal>
      </section>

      {/* NEWSLETTER */}
      <section className="wit-section-block pb-16 relative">
        <Reveal>
          <div className="relative wit-glass-strong p-8 sm:p-12 flex flex-col items-center text-center gap-5 overflow-hidden">
            <SparkleEyebrow className="wit-eyebrow text-raspberry justify-center">{t("home.newsletterEyebrow")}</SparkleEyebrow>
            <SectionHeading title={t("home.newsletterTitle")} subtitle={t("home.newsletterBody")} align="center" />
            <NewsletterForm testId="home-newsletter" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
